import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useAllTemplatesQuery$data,
	useAllTemplatesQuery$variables,
} from './__generated__/useAllTemplatesQuery.graphql.ts';

const allTemplatesQuery = graphql`
	query useAllTemplatesQuery {
		allTemplates(usePersonalSpace: true) {
			nodes {
				blueprintModuleCompleteKey
				name
			}
		}
	}
`;

export type BlueprintModuleKeyToLocalizedName = Record<string, string>;

/**
 * Builds a lookup map from blueprintModuleCompleteKey to the server-localized template name.
 * This allows blueprint template cards to display translated names based on the user's locale.
 */
export const mapBlueprintModuleKeyToLocalizedName = (
	data?: useAllTemplatesQuery$data,
): BlueprintModuleKeyToLocalizedName => {
	const map: BlueprintModuleKeyToLocalizedName = {};
	data?.allTemplates?.nodes?.forEach((template) => {
		if (template?.blueprintModuleCompleteKey && template?.name) {
			map[template.blueprintModuleCompleteKey] = template.name;
		}
	});
	return map;
};

export interface UseAllTemplatesQueryResult {
	blueprintModuleKeyToLocalizedName: BlueprintModuleKeyToLocalizedName;
	loading: boolean;
	error?: Error;
}

/**
 * Fetches all templates from the server and builds a lookup map from
 * blueprintModuleCompleteKey to the server-localized template name.
 * This is used to display translated blueprint template names in the TemplateModal.
 */
export const useAllTemplatesQuery = ({
	skip,
}: { skip?: boolean } = {}): UseAllTemplatesQueryResult => {
	const { data, loading, error } = useQueryInterop<
		useAllTemplatesQuery$data,
		useAllTemplatesQuery$variables
	>(allTemplatesQuery, {
		skip,
	});

	const blueprintModuleKeyToLocalizedName = mapBlueprintModuleKeyToLocalizedName(data);

	return {
		blueprintModuleKeyToLocalizedName,
		loading,
		error: error as Error | undefined,
	};
};
